import React, { useEffect, useState, useRef } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Button, Input, Select, Pill } from "../../components/common/Primitives";
import api, { formatApiError, API_BASE } from "../../lib/api";
import useFocusHighlight from "../../lib/useFocusHighlight";
import { Upload, Trash2, FileText, Download } from "lucide-react";

const categories = [
  "Pitch deck", "Financial statements", "MIS", "Projections", "Cap table",
  "Incorporation documents", "GST returns", "ITRs", "Bank statements",
  "Customer contracts", "Vendor agreements", "IP documents", "Licenses",
  "Debt schedule", "Existing investor documents", "Litigation declaration", "Compliance declaration",
];

const toneFor = (s) => {
  if (s === "Accepted") return "success";
  if (s === "Rejected") return "danger";
  if (s === "On Hold") return "warn";
  if (s === "Resubmission Requested") return "warn";
  return "muted";
};

function formatBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function StartupDocuments() {
  useFocusHighlight();
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState({ category: categories[0], note: "" });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    const r = await api.get("/startup/documents");
    setDocs(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file first.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("category", form.category);
      fd.append("note", form.note || "");
      fd.append("file", file);
      await api.post("/startup/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded.`);
      setFile(null);
      setForm({ category: categories[0], note: "" });
      if (inputRef.current) inputRef.current.value = "";
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/startup/documents/${id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.doc_id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardShell title="Document Vault" subtitle="STARTUP · DATA ROOM">
      <Toaster richColors position="top-right" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={upload} className="border border-white/10 bg-white/70 backdrop-blur p-6 space-y-4" data-testid="doc-upload-form">
          <div className="overline">Upload Document</div>
          <Select label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} testId="doc-category">
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
          <div>
            <div className="overline mb-1.5">File</div>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.csv,.txt"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-ink-soft file:mr-3 file:px-3 file:py-2 file:border-0 file:bg-gold file:text-ink-bg file:font-heading file:font-semibold file:cursor-pointer file:hover:bg-gold-hover"
              data-testid="doc-file-input"
            />
            {file && <div className="text-[11px] font-mono text-ink-muted mt-2">{file.name} · {formatBytes(file.size)}</div>}
          </div>
          <Input label="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} testId="doc-note" />
          <div className="text-[11px] text-ink-faint leading-relaxed">Max 25 MB · PDF / DOC / XLS / images accepted.</div>
          <Button type="submit" disabled={uploading || !file} className="w-full flex items-center justify-center gap-2" data-testid="doc-upload-btn">
            <Upload size={14} /> {uploading ? "Uploading…" : "Upload"}
          </Button>
        </form>

        <div className="lg:col-span-2 border border-white/10 bg-white/70 backdrop-blur" data-testid="docs-list">
          <div className="p-4 border-b border-white/10 overline">Uploaded Documents · {docs.length}</div>
          {docs.length === 0 ? (
            <div className="p-10 text-center text-ink-muted text-sm">No documents uploaded yet.</div>
          ) : (
            <div className="divide-y divide-white/10">
              {docs.map((d) => (
                <div key={d.doc_id} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText size={16} className="text-gold shrink-0" />
                    <div className="min-w-0">
                      <div className="text-ink text-sm font-heading truncate">{d.filename}</div>
                      <div className="text-[11px] font-mono text-ink-muted uppercase tracking-wider">
                        {d.category}{d.size ? ` · ${formatBytes(d.size)}` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Pill tone={toneFor(d.status)}>{d.status}</Pill>
                    <button onClick={() => download(d)} className="text-ink-muted hover:text-gold transition-colors" data-testid={`doc-download-${d.doc_id}`} title="Download"><Download size={14} /></button>
                    <button onClick={() => remove(d.doc_id)} className="text-ink-muted hover:text-rose-400 transition-colors" data-testid={`doc-delete-${d.doc_id}`} title="Delete"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
